import {Svg, Path} from 'react-native-svg';
import {useLingui} from '@lingui/react/macro';
import {useSelector} from 'react-redux';
import {useRef, Fragment} from 'react';
import {useWindowDimensions, StyleSheet, View, Text, Pressable, ViewStyle} from 'react-native';
import {useDragDropTarget} from 'interface/hooks/useDragDropTarget';
import {SafeAreaView} from 'extensions/viewport/SafeArea';
import {DropFrame} from 'interface/base/DropFrame';
import {FileList} from 'interface/stacks/FileList';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {useUploader} from 'interface/hooks/useUploader';
import {ProgressBar} from 'interface/base/ProgressBar';
import {IconButton} from 'interface/base/IconButton';
import {bytesize} from 'utils/common/data';
import {isWeb, isIOS, isTouch} from 'utils/platform';
import {colors, vectors} from 'config/styles';
import {theme} from 'config/themes';

import * as app from 'store/slices/app';
import * as transfers from 'store/slices/transfers';

export interface TransferUploadsProps {
  id: string;
}

export function TransferUploads(props: TransferUploadsProps) {
  const {t} = useLingui();
  const dndRef = useRef<View>(null);
  const screen = useWindowDimensions();
  const uploader = useUploader(props.id);
  const isPremium = useSelector(app.selectors.isPremium);
  const minimized = useSelector(transfers.selectors.isUploaderMinimized);
  const isComplete = uploader.activated && uploader.status.completed;
  const isMinimal = screen.width < 800 || screen.height < 520;
  const isSmall = screen.width < 600;
  const isFileDrop = false;
  const hasFiles = uploader.status.count > 0;
  const hasAborted = hasFiles && uploader.status.aborted === uploader.status.count;
  const hasSkipped = hasFiles && uploader.status.skipped === uploader.status.count;
  const hasWebUpload = isPremium || !isIOS();
  const hasFolderUpload = !isTouch();
  const hasButtonText = screen.width < 530;

  const textFileDrop = 'Upload to my MediaFire FileDrop';
  const textSizes = `${bytesize(uploader.status.uploaded, 2)} / ${bytesize(uploader.status.size, 2)}`;
  const textCount = `${uploader.status.count} File${uploader.status.count !== 1 ? 's' : ''}`;
  const textSkipped = `${uploader.status.skipped} file${uploader.status.skipped !== 1 ? 's' : ''} not uploaded`;
  const textFinished = `${uploader.status.finished} file${uploader.status.finished !== 1 ? 's' : ''} uploaded`;
  const textUpload = uploader.progress.uploaded && !isNaN(uploader.progress.uploaded)
    ? (uploader.status.verifying > 0 && uploader.progress.uploaded >= 100)
      ? `Verifying ${uploader.status.verifying} file${uploader.status.verifying !== 1 ? 's' : ''}…`
      : `${Math.floor(uploader.progress.uploaded)}%`
    : uploader.activated
      ? hasSkipped 
        ? textSkipped
        : 'Preparing…'
      : 'Queued';

  const textTitle = isComplete
    ? hasSkipped
      ? hasAborted
        ? t`Upload Skipped`
        : t`Upload Failed`
      : t`Upload Completed`
    : uploader.activated
      ? t`Uploading ${textCount}`
      : uploader.status.count === 0
        ? isMinimal ? t`Select Files` : t`Select Files to Upload`
        : t`Selected ${textCount}`;

  const classes = {
    root: [
      styles.root,
      minimized && styles.minimized,
      minimized && isSmall && styles.minimizedSmall,
      !minimized && styles.maximized,
    ],
    uploader: [
      isMinimal ? styles.fullscreen : styles.uploader,
    ],
  };

  const webFileInput = (
    <Fragment>
      <input
        multiple
        onChange={uploader.addFilesInput(props.id)}
        type="file"
        tabIndex={-1}
      /><label/>
    </Fragment>
  );

  const webFolderInput = (
    <Fragment>
      <input
        type="file"
        tabIndex={-1}
        onChange={uploader.addFolder}
        // @ts-ignore
        directory={'true'}
        webkitdirectory={'true'}
        mozdirectory={'true'}
      /><label/>
    </Fragment>
  );

  const {isDropping} = useDragDropTarget({
    ref: dndRef,
    targetId: props.id,
    targetName: 'Uploads',
    options: {
      isUploadOnly: true,
      queueUploads: true,
    },
  });

  return (
    <Pressable
      style={classes.root}
      onPress={!minimized
        ? isComplete || !hasFiles
          ? uploader.exit
          : uploader.minimize
        : uploader.noop}>
      {minimized
        ? <Pressable style={styles.uploaderMin} onPress={uploader.maximize}>
            <ProgressBar
              items={[
                {value: uploader.progress.hashed, color: theme.transfers.progress.hashed},
                {value: uploader.progress.uploaded, color: theme.transfers.progress.uploaded},
                hasSkipped && {value: 100, color: colors.secondary.redOrange},
              ]}
              texts={[
                uploader.activated && {
                  value: (!isComplete || uploader.status.verifying > 0) ? textSizes : textTitle,
                  style: [newStyles.text, newStyles.minimized, newStyles.left],
                }, {
                  value: textUpload,
                  style: [newStyles.text, newStyles.minimized, newStyles.right],
                },
              ]}
            />
          </Pressable>
        : <Pressable
            ref={dndRef}
            style={classes.uploader}
            onPress={uploader.noop}>
            <SafeAreaView style={{flex: 1, backgroundColor: colors.neutral.black}}>
              <View style={styles.contents}>
                <View style={styles.header}>
                  <Text style={styles.title}>
                    {textTitle}
                  </Text>
                  {uploader.activated && !isComplete
                    ? <Pressable onPress={uploader.minimize}>
                        <View style={styles.buttonClose}>
                          <Svg viewBox="0 0 24 24" width={24} height={24}>
                            <Path d={vectors.icons.min} fill={colors.neutral.mid}/>
                          </Svg>
                        </View>
                      </Pressable>
                    : <Pressable onPress={uploader.exit}>
                        <View style={styles.buttonClose}>
                          <Svg viewBox="0 0 14 14" width={14} height={14}>
                            <Path d={vectors.icons.close} fill={colors.neutral.mid}/>
                          </Svg>
                        </View>
                      </Pressable>
                  }
                </View>
                {hasFiles &&
                  <FileList
                    view={uploader.view}
                    items={uploader.items}
                    layout={FileListItemLayout.ListNormal}
                    options={{
                      hideHeader: true,
                      hideGutter: true,
                      disableOpen: true,
                      disableDrag: true,
                      disableFocus: true,
                      disableSelect: true,
                      disablePullToRefresh: true,
                    }}
                  />
                }
                {!hasFiles &&
                  <Pressable
                    style={styles.empty}
                    onPress={!isWeb() ? uploader.addMedia(props.id) : undefined}>
                    {isWeb() && webFileInput}
                    {isTouch() && <Text style={styles.touchHint}>{t`Tap here to browse media to upload`}</Text>}
                    {isFileDrop && <Text style={styles.fileDrop}>{textFileDrop}</Text>}
                    {isWeb() && !isTouch() &&
                      <View style={styles.emptyIcon}>
                        <Svg viewBox="0 0 14 14" width={64} height={64}>
                          <Path d={vectors.icons.add} fill={theme.transfers.icon}/>
                        </Svg>
                      </View>
                    }
                  </Pressable>
                }
                <View style={styles.actions}>
                  {isComplete &&
                    <View style={[styles.buttonUpload, {cursor: 'default'} as ViewStyle]}>
                      <Svg viewBox="0 0 20 20" width={20} height={20}>
                        <Path
                          d={uploader.status.skipped === 0 ? vectors.icons.check : vectors.icons.warning}
                          fill={uploader.status.skipped === 0 ? colors.secondary.green : colors.secondary.redOrange}
                        />
                      </Svg>
                      <Text style={styles.completeMessage}>
                        {uploader.status.skipped === 0 ? textFinished : textSkipped}
                      </Text>
                    </View>
                  }
                  {!uploader.activated &&
                    <View style={styles.input}>
                      {isWeb() && webFileInput}
                      <Pressable onPress={uploader.addFiles(props.id)}>
                        <View style={styles.buttonUpload}>
                          <Svg viewBox="0 0 24 24" width={24} height={24}>
                            <Path d={vectors.icons.fileAdd} fill={theme.transfers.headerText} fillOpacity={0.8}/>
                          </Svg>
                          {!hasButtonText &&
                            <Text style={styles.buttonUploadText}>
                              {t`Add file`}
                            </Text>
                          }
                        </View>
                      </Pressable>
                    </View>
                  }
                  {hasFolderUpload && !uploader.activated &&
                    <View style={styles.input}>
                      {isWeb() && webFolderInput}
                      <Pressable onPress={uploader.addFiles(props.id)}>
                        <View style={styles.buttonUpload}>
                          <Svg viewBox="0 0 24 18" width={24} height={18}>
                            <Path d={vectors.icons.folderAdd} fill={theme.transfers.headerText} fillOpacity={0.8}/>
                          </Svg>
                          {!hasButtonText &&
                            <Text style={styles.buttonUploadText}>
                              {t`Add folder`}
                            </Text>
                          }
                        </View>
                      </Pressable>
                    </View>
                  }
                  {hasWebUpload && !uploader.activated &&
                    <Pressable onPress={isPremium ? uploader.modalWebUpload.open : uploader.showUpsellWebUpload}>
                      <View style={styles.buttonUpload}>
                        <Svg viewBox="0 0 20 20" width={20} height={20}>
                          <Path d={vectors.icons.web} fill={theme.transfers.headerText} fillOpacity={0.8}/>
                        </Svg>
                        {!hasButtonText &&
                          <Text style={styles.buttonUploadTextWeb}>
                            {t`Web Upload`}
                          </Text>
                        }
                      </View>
                    </Pressable>
                  }
                  {uploader.activated && (hasSkipped || isComplete) &&
                    <View style={styles.spacer}/>
                  }
                  {uploader.activated && !isComplete && !hasSkipped ? (
                    <View style={styles.activeUploadContainer}>
                      <View style={styles.progress}>
                        <ProgressBar
                          items={[
                            {value: uploader.progress.hashed, color: theme.transfers.progress.hashed},
                            {value: uploader.progress.uploaded, color: theme.transfers.progress.uploaded},
                            hasSkipped ? {value: 100, color: colors.secondary.redOrange} : undefined,
                          ]}
                          texts={[
                            {value: textSizes, style: [newStyles.text, newStyles.main, newStyles.left]},
                            {value: textUpload, style: [newStyles.text, newStyles.main, newStyles.right]},
                          ]}
                        />
                      </View>
                      <IconButton
                        mode="cancel"
                        onPress={uploader.exit}
                        title={t`Close uploader`}
                        text={t`Cancel`.toUpperCase()}
                      />
                    </View>
                  ) : (
                    <View style={!uploader.activated ? styles.buttonsQueue : styles.buttonsActive}>
                      {!uploader.activated && !isComplete &&
                        <Fragment>
                          <IconButton
                            mode="primary"
                            disabled={!hasFiles && isWeb()}
                            onPress={hasFiles ? uploader.start : uploader.addFiles(props.id)}
                            title={t`Start upload`}
                            text={t`Upload`.toUpperCase()}
                          />
                          <View style={styles.spacerH}/>
                        </Fragment>
                      }
                      <IconButton
                        mode="cancel"
                        onPress={uploader.exit}
                        title={t`Close uploader`}
                        text={isComplete
                          ? t`Close`.toUpperCase()
                          : t`Cancel`.toUpperCase()
                        }
                      />
                    </View>
                  )}
                </View>
              </View>
            </SafeAreaView>
            {isDropping && <DropFrame stroke="medium"/>}
          </Pressable>
      }
    </Pressable>
  );
}

export const newStyles = StyleSheet.create({
  text: {
    flex: 0,
    fontSize: 12,
  },
  main: {
    color: theme.transfers.headerText,
  },
  minimized: {
    color: colors.neutral.light,
  },
  left: {
    flex: 1,
  },
  right: {
    flex: 0,
  },
});

export const styles = StyleSheet.create({
  // Containers
  root: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.dialog.overlay,
    // @ts-ignore
    cursor: 'default',
    left: 0,
    right: 0,
  },
  maximized: {
    top: 0,
    bottom: 0,
  },
  minimized: {
    width: 350,
    height: 62,
    borderTopWidth: 1,
    borderTopLeftRadius: 3,
    borderTopRightRadius: 3,
    backgroundColor: colors.neutral.darker,
    borderColor: colors.neutral.dark,
    shadowOffset: {width: -1, height: -1},
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 3,
    elevation: 1,
    bottom: 0,
    right: 16,
    // @ts-ignore
    left: 'initial',
  },
  minimizedSmall: {
    left: 0,
    right: 0,
    width: undefined,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  uploader: {
    width: 780,
    height: 500,
    borderRadius: 3,
    // @ts-ignore
    cursor: 'default',
    backgroundColor: theme.transfers.fill,
    shadowOffset: {width: 0, height: 3},
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 3,
    elevation: 1,
  },
  fullscreen: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.transfers.fill,
  },
  uploaderMin: {
    flex: 1,
    padding: 18,
    paddingTop: 16,
    paddingBottom: 12,
    // cursor: 'pointer',
    flexDirection: 'column',
  },
  progress: {
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: 18,
  },
  // Sections
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 65,
    borderBottomWidth: 1,
    borderColor: theme.transfers.headerBorder,
    backgroundColor: theme.transfers.header,
  },
  contents: {
    flex: 1,
    backgroundColor: theme.transfers.fill,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 62,
    paddingRight: 16,
    borderTopWidth: 1,
    borderColor: theme.transfers.headerBorder,
    backgroundColor: theme.transfers.header,
  },
  activeUploadContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  // Empty
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.transfers.fill,
  },
  emptyIcon: {
    width: 180,
    height: 180,
    borderWidth: 3,
    borderRadius: 5,
    borderStyle: 'dashed',
    borderColor: theme.transfers.icon,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Texts
  title: {
    color: theme.transfers.headerTitle,
    paddingLeft: 20,
    fontSize: 16,
    flex: 1,
  },
  fileDrop: {
    marginBottom: 16,
    fontSize: 20,
    fontWeight: '600',
    color: theme.transfers.headerText,
  },
  touchHint: {
    marginBottom: 32,
    fontSize: 16,
    fontWeight: '500',
    color: theme.transfers.headerText,
  },
  completeMessage: {
    color: theme.transfers.headerText,
    marginLeft: 8,
    fontSize: 14,
  },
  // Buttons
  buttonsQueue: {
    flex: 1,
    marginVertical: 8,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  buttonsActive: {
    flex: 0,
    marginVertical: 8,
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  buttonClose: {
    alignItems: 'center',
    justifyContent: 'center',
    width: 65,
    height: 65,
    top: 1,
  },
  buttonUpload: {
    flex: 1,
    marginRight: 6,
    marginLeft: 16,
    height: 60,
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonUploadText: {
    color: theme.transfers.headerText,
    marginLeft: 4,
    fontSize: 14,
  },
  buttonUploadTextWeb: {
    color: theme.transfers.headerText,
    marginHorizontal: 7,
    marginBottom: 1,
    fontSize: 14,
  },
  // Helpers
  input: {
    flex: 0.3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spacer: {
    flex: 1,
  },
  spacerH: {
    width: 6,
  },
});
